import {Injectable} from '@angular/core';

import {Observable, of as observableOf} from 'rxjs';
import {map} from 'rxjs/operators';

import {AirBookingService} from './airBooking.service';
import {HotelBookingService} from './hotelBooking.service';
import {OperationStatus, TypeStatus} from 'libraries/service/resources/model/search/IBooking';
import {IAirTotal} from 'libraries/service/resources/model/search/air/IAirTotal';

import {Yii} from "libraries/interfaces/Yii";
import {ICodeName} from 'libraries/service/resources/model/shared/ICodeName';
import {SsrsService} from '../ssrs/Ssrs.service';
import {IBookingWithType} from './IBookingWithType';
import {
    ApiActionForFareOptimisation,
    ApiEligibility, ApiImpactOnFareType, ApiImpactOnLuggage,
    ApiIssueStatus, ApiMembershipCard,
    ApiMidOffice,
    ApiPricingRate,
    ApiPricingXRank,
    ApiPricingXState, ApiSeatAssignment,
    ApiServiceXStatus,
    ApiXType,
} from '../resources/booking/interfaces/ApiSearchParameters';
import {IGetBookingsCommonResponse} from '../resources/booking/interfaces/IGetBookings';
import {VehicleRentalBookingService} from "./vehicleRentalBooking.service";
import {IQualityCheckControl, IQualityCheckDictionary} from "./IQualityCheck";
import {EnumsResourceService} from "./enums/enums-resource.service";

@Injectable()
export class BookingService {

    public midOfficeLabels: { [key in ApiMidOffice]: string } = {
        [ApiMidOffice.Confirm]: Yii.t('wui-shared-js', "Confirmé"),
        [ApiMidOffice.Cancel]: Yii.t('wui-shared-js', "Annulé"),
        [ApiMidOffice.Option]: Yii.t('wui-shared-js', "Option"),
    };

    public xTypeLabels: { [key in ApiXType]: string } = {
        [ApiXType.Group]: Yii.t('wui-shared-js', "Groupe"),
        [ApiXType.Individual]: Yii.t('wui-shared-js', "Individuel"),
    };

    public issueStatusLabels: { [key in ApiIssueStatus]: string } = {
        [ApiIssueStatus.NotIssued]: Yii.t('wui-shared-js', "Non émis"),
        [ApiIssueStatus.PartiallyIssued]: Yii.t('wui-shared-js', "Partiellement émis"),
        [ApiIssueStatus.CompletelyIssued]: Yii.t('wui-shared-js', "Émis"),
    };

    public pricingRateLabels: { [key in ApiPricingRate]: string } = {
        [ApiPricingRate.Public]: Yii.t('wui-shared-js', "Public"),
        [ApiPricingRate.Negotiated]: Yii.t('wui-shared-js', "Négocié"),
    };

    public pricingXStateLabels: { [key in ApiPricingXState]: string } = {
        [ApiPricingXState.Guaranteed]: Yii.t('wui-shared-js', 'Garanti'),
        [ApiPricingXState.Expired]: Yii.t('wui-shared-js', 'Expiré'),
        [ApiPricingXState.Invalid]: Yii.t('wui-shared-js', 'Invalide'),
        [ApiPricingXState.NotGuaranted]: Yii.t('wui-shared-js', 'Non garanti'),
        [ApiPricingXState.Issued]: Yii.t('wui-shared-js', 'Émis'),
        [ApiPricingXState.Manual]: Yii.t('wui-shared-js', 'Saisie manuelle'),
    };

    public pricingXRankLabels: { [key in ApiPricingXRank]: string } = {
        [ApiPricingXRank.external]: Yii.t('wui-shared-js', "externe"),
        [ApiPricingXRank.real]: Yii.t('wui-shared-js', "réel"),
    };

    public serviceXStatusLabels: { [key in ApiServiceXStatus]: string } = {
        [ApiServiceXStatus.Confirm]: Yii.t('wui-shared-js', 'Confirmé'),
        [ApiServiceXStatus.Request]: Yii.t('wui-shared-js', 'En demande'),
        [ApiServiceXStatus.NoAction]: Yii.t('wui-shared-js', 'Aucune action'),
        [ApiServiceXStatus.Unknown]: Yii.t('wui-shared-js', 'Inconnu'),
        [ApiServiceXStatus.Cancel]: Yii.t('wui-shared-js', 'Annulé'),
        [ApiServiceXStatus.UnableToConfirm]: Yii.t('wui-shared-js', 'Impossible à confirmer'),
        [ApiServiceXStatus.Failed]: Yii.t('wui-shared-js', 'Echoué'),
    };

    public eligibilityLabels: { [key in ApiEligibility]: string } = {
        [ApiEligibility.Inhibited]: Yii.t('wui-shared-js', 'Inhibés '),
        [ApiEligibility.NotApplicable]: Yii.t('wui-shared-js', 'Non éligibles'),
        [ApiEligibility.Applicable]: Yii.t('wui-shared-js', 'Éligibles globalement'),
        [ApiEligibility.ApplicableNow]: Yii.t('wui-shared-js', 'Éligibles maintenant'),
        [ApiEligibility.ApplicableLater]: Yii.t('wui-shared-js', 'Éligibles plus tard'),
    };

    public operationLabels: { [key in OperationStatus]: string } = {
        [OperationStatus.success]: Yii.t('wui-shared-js', 'Succès'),
        [OperationStatus.warning]: Yii.t('wui-shared-js', 'Avertissement'),
        [OperationStatus.error]: Yii.t('wui-shared-js', 'Erreur'),
    };

    public typeLabels: { [key in TypeStatus]: string } = {
        [TypeStatus.air]: Yii.t('wui-shared-js', 'Aérien'),
        [TypeStatus.stay]: Yii.t('wui-shared-js', 'Hébergement'),
        [TypeStatus.vehicleRental]: Yii.t('wui-shared-js', 'Location de voiture'),
    };

    public actionForFareOptimisationLabels: { [key in ApiActionForFareOptimisation]: string } = {
        [ApiActionForFareOptimisation.Rebook]: Yii.t('wui-shared-js', 'REBOOK'),
        [ApiActionForFareOptimisation.Reprice]: Yii.t('wui-shared-js', 'REPRICE'),
        [ApiActionForFareOptimisation.Split]: Yii.t('wui-shared-js', 'SPLIT'),
    };

    public impactOnLuggageLabels: { [key in ApiImpactOnLuggage]: string } = {
        [ApiImpactOnLuggage.Same]: Yii.t('wui-shared-js', 'Identique'),
        [ApiImpactOnLuggage.Different]: Yii.t('wui-shared-js', 'Différent'),
        [ApiImpactOnLuggage.LuggageInHold2NoLuggageInHold]: Yii.t('wui-shared-js', 'Inclus > Non inclus'),
        [ApiImpactOnLuggage.NoLuggageInHold2LuggageInHold]: Yii.t('wui-shared-js', 'Non inclus > inclus'),
    };

    public impactOnFareTypeLabels: { [key in ApiImpactOnFareType]: string } = {
        [ApiImpactOnFareType.NegoFare2PublicFare]: Yii.t('wui-shared-js', 'Négocié > Public'),
        [ApiImpactOnFareType.Public2NegoFare]: Yii.t('wui-shared-js', 'Public > Négocié'),
        [ApiImpactOnFareType.Public2Public]: Yii.t('wui-shared-js', 'Public > Public'),
        [ApiImpactOnFareType.NegoFare2NegoFare]: Yii.t('wui-shared-js', 'Négocié > Négocié'),
    };

    public membershipLabels: { [key in ApiMembershipCard]: string } = {
        [ApiMembershipCard.True]: Yii.t('wui-shared-js', 'Avec'),
        [ApiMembershipCard.False]: Yii.t('wui-shared-js', 'Sans'),
    };

    public seatAssignmentLabels: { [key in ApiSeatAssignment]: string } = {
        [ApiSeatAssignment.True]: Yii.t('wui-shared-js', 'Avec'),
        [ApiSeatAssignment.False]: Yii.t('wui-shared-js', 'Sans'),
    };

    private readonly unknownLabel: string = Yii.t('wui-shared-js', "Inconnu");

    constructor(
        private readonly airBookingService: AirBookingService,
        private readonly hotelBookingService: HotelBookingService,
        private readonly vehicleRentalBookingService: VehicleRentalBookingService,
        private readonly ssrsService: SsrsService,
        private readonly enumsResourceService: EnumsResourceService,
    ) {
    }

    public isAirBooking(booking: IBookingWithType): boolean {
        return this.airBookingService.isAirBooking(booking);
    }

    public isHotelBooking(booking: IBookingWithType): boolean {
        return this.hotelBookingService.isHotelBooking(booking);
    }

    public isVehicleRentalBooking(booking: IBookingWithType): boolean {
        return this.vehicleRentalBookingService.isVehicleRentalBooking(booking);
    }

    public getMidOfficeLabel(midOffice: string): Observable<string> {
        if (!midOffice || !this.midOfficeLabels[midOffice]) {
            return observableOf(this.unknownLabel);
        }

        return observableOf(this.midOfficeLabels[midOffice]);
    }

    public getXTypeLabel(xType: string): Observable<string> {
        if (!xType || !this.xTypeLabels[xType]) {
            return observableOf(this.unknownLabel);
        }

        return observableOf(this.xTypeLabels[xType]);
    }

    public getIssueStatusLabel(issueStatus: string): Observable<string> {
        if (!issueStatus || !this.issueStatusLabels[issueStatus]) {
            return observableOf(this.unknownLabel);
        }

        return observableOf(this.issueStatusLabels[issueStatus]);
    }

    public getPricingRateLabel(pricingRate: string): Observable<string> {
        if (!pricingRate || !this.pricingRateLabels[pricingRate]) {
            return observableOf(this.unknownLabel);
        }

        return observableOf(this.pricingRateLabels[pricingRate]);
    }

    public getPricingXStateLabel(pricingXState: string): string {
        if (!pricingXState || !this.pricingXStateLabels[pricingXState]) {
            return this.unknownLabel;
        }

        return this.pricingXStateLabels[pricingXState];
    }

    public getPricingXRankLabel(pricingXRank: string): string {
        if (!pricingXRank || !this.pricingXStateLabels[pricingXRank]) {
            return this.unknownLabel;
        }

        return this.pricingXRankLabels[pricingXRank];
    }

    public getServiceCodeLabel(code: string): Observable<string> {
        return this.ssrsService.get(code).pipe(map((item: ICodeName) => item && item.name || code));
    }

    public getServiceXStatusLabel(serviceXStatus: string): Observable<string> {
        if (!serviceXStatus || !this.serviceXStatusLabels[serviceXStatus]) {
            return observableOf(this.unknownLabel);
        }

        return observableOf(this.serviceXStatusLabels[serviceXStatus]);
    }

    public getPrice(booking: IGetBookingsCommonResponse, currency: string): IAirTotal[] {
        return booking &&
            booking.total &&
            booking.total.filter(
                (total: IAirTotal) => total.currency === currency
            );
    }

    public getEligibilityLabel(eligibility: string): string {
        if (!eligibility || !this.eligibilityLabels[eligibility]) {
            return this.unknownLabel;
        }
        return this.eligibilityLabels[eligibility];
    }

    public getOperationLabel(operation: string): string {
        if (!operation || !this.operationLabels[operation]) {
            return this.unknownLabel;
        }
        return this.operationLabels[operation];
    }

    public getTypeLabel(type: string): string {
        if (!type) {
            return this.unknownLabel;
        }
        if (!this.typeLabels[type.toLowerCase()]) {
            return type;
        }
        return this.typeLabels[type.toLowerCase()];
    }

    public getQualityCheckLabel(controlCode: string): Observable<string> {
        return this.enumsResourceService.listQualityCheck()
            .pipe(map((qualityCheckList: IQualityCheckDictionary) => {
                const qualityCheckControl: IQualityCheckControl = qualityCheckList.qualityCheckControlArray.find((control: IQualityCheckControl) => control.code === controlCode);
                if (qualityCheckControl) {
                    return qualityCheckControl.nameArray?.length ? qualityCheckControl.nameArray[0].value : qualityCheckControl.code;
                }
                return controlCode;
            }));
    }

    public getImpactOnFareTypeLabel(type: string): string {
        console.log('type ', type);
        if (!type) {
            return this.unknownLabel;
        }
        if (!this.impactOnFareTypeLabels[type]) {
            return type;
        }
        return this.impactOnFareTypeLabels[type];
    }

    public getImpactOnLuggageLabel(type: string): string {
        if (!type) {
            return this.unknownLabel;
        }
        if (!this.impactOnLuggageLabels[type]) {
            return type;
        }
        return this.impactOnLuggageLabels[type];
    }

    public getHasMembershipCardLabel(type: string): string {
        if (!type) {
            return this.unknownLabel;
        }
        if (!this.membershipLabels[type]) {
            return type;
        }
        return this.membershipLabels[type];
    }

    public getHasSeatAssignmentLabel(type: string): string {
        if (!type) {
            return this.unknownLabel;
        }
        if (!this.seatAssignmentLabels[type]) {
            return type;
        }
        return this.seatAssignmentLabels[type];
    }
}
